import {AppUrls} from 'shared/config/model/urls';

import {MouseEvent, useCallback} from 'react';
import {generatePath, useNavigate} from 'react-router-dom';

import {MonitorRowClickHandler, useMonitorRowSelection, useMonitorRowState} from 'entities/monitor';
import {DomainMonitorModalType, getDomainMonitorDateFormat} from 'features/domain/lib/domainMonitorView';
import useToggleTagFilter from 'shared/lib/hooks/useToggleTagFilter';
import {isTranslate} from 'shared/lib/lang';

import IDomainMonitorOutput from './DomainMonitor/IDomainMonitor';
import {domainMonitorSelectedIdsSelector, toggleOne} from './domainMonitorSlice';
import useCanCreateDomainMonitor from './useCanCreateDomainMonitor';
import useDomainMonitorToggle from './useDomainMonitorToggle';

export interface IUseDomainMonitorRow {
    activeModal: DomainMonitorModalType,
    closeModal: () => void,
    canCreate: boolean,
    isSelected: boolean,
    dateFormat: string,
    onCheckboxChange: () => void,
    onTagClick: (_tag: string) => void,
    onDetailsClick: MonitorRowClickHandler,
    onEditClick: MonitorRowClickHandler,
    onPauseClick: MonitorRowClickHandler,
    onResumeClick: MonitorRowClickHandler,
    onDeleteClick: MonitorRowClickHandler,
    onTagsClick: MonitorRowClickHandler,
    onToggleClick: (_event: MouseEvent<HTMLDivElement>) => Promise<void>,
}

export const useDomainMonitorRow = (item: IDomainMonitorOutput): IUseDomainMonitorRow => {
    const {activeModal, setActiveModal, closeModal, openModal} = useMonitorRowState<DomainMonitorModalType>();
    const {isSelected, onCheckboxChange} = useMonitorRowSelection(
        item.uuid,
        domainMonitorSelectedIdsSelector,
        toggleOne,
    );

    const navigate = useNavigate();
    const onTagClick = useToggleTagFilter();
    const canCreate = useCanCreateDomainMonitor();

    const onDetailsClick: MonitorRowClickHandler = (event) => {
        event.stopPropagation();
        navigate(generatePath(AppUrls.DOMAIN_MONITOR_DETAILS, {domainMonitorId: item.uuid}));
    };

    const onToggleClick = useDomainMonitorToggle(item, useCallback(() => setActiveModal('limit'), [setActiveModal]));

    return {
        activeModal,
        closeModal,
        canCreate,
        isSelected,
        dateFormat: getDomainMonitorDateFormat(isTranslate()),
        onCheckboxChange,
        onTagClick,
        onDetailsClick,
        onEditClick: openModal('edit'),
        onPauseClick: openModal('pause'),
        onResumeClick: openModal('resume'),
        onDeleteClick: openModal('delete'),
        onTagsClick: openModal('tags'),
        onToggleClick,
    };
};

export default useDomainMonitorRow;
